import { PaperClipIcon } from '@heroicons/react/24/outline';
import { Tooltip } from '@odo/shared-ui';
import type { QuickReplyItem } from './quickReplyItem';

const UNAVAILABLE_MEDIA_HINT =
    "This quick reply's media isn't available, so only the text is inserted.";

function mediaHint(reply: QuickReplyItem): string {
    return reply.media ? `Attaches "${reply.media.title}"` : UNAVAILABLE_MEDIA_HINT;
}

interface QuickReplyRowProps<T extends QuickReplyItem> {
    id: string;
    index: number;
    reply: T;
    active: boolean;
    onHover: (index: number) => void;
    onPick: (reply: T) => void;
}

function QuickReplyRow<T extends QuickReplyItem>({
    id,
    index,
    reply,
    active,
    onHover,
    onPick
}: QuickReplyRowProps<T>) {
    return (
        <li
            id={id}
            role="option"
            aria-selected={active}
            data-index={index}
            onMouseEnter={() => onHover(index)}
            // Keeps focus in the search input so the keyboard flow survives a click.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onPick(reply)}
            className={`px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                active ? 'bg-gray-100' : 'hover:bg-gray-50'
            }`}
        >
            <div className="flex items-center gap-1.5 min-w-0">
                <span className="text-sm font-semibold text-gray-800 truncate">{reply.title}</span>
                {(reply.hasMedia || reply.media) && (
                    <Tooltip content={mediaHint(reply)} className="!z-[9001]">
                        <span className="flex-shrink-0 text-gray-400">
                            <PaperClipIcon className="w-3.5 h-3.5" aria-hidden="true" />
                            <span className="sr-only">{mediaHint(reply)}</span>
                        </span>
                    </Tooltip>
                )}
                {reply.code && (
                    <span className="ml-auto flex-shrink-0 font-mono text-[11px] text-gray-400">
                        {reply.code}
                    </span>
                )}
            </div>
            <p className="text-xs text-gray-500 line-clamp-2 whitespace-pre-line break-words mt-0.5">
                {reply.content}
            </p>
        </li>
    );
}

export { QuickReplyRow };
